import Collapse from '@material-ui/core/Collapse';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import { CircularProgress, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';

import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';

import { unoTheme } from '../../../../assets/styles/unoTheme';
import { PortfolioChangesAPI } from '../../../../API/portfolio_changes';
import MyTable from '../../../utils/MyTable';
import { applyMaskAmount, getMonthName } from '../../../utils/utils';

const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
    },

    warningBar: {
        display: 'flex',
        alignItems: 'center',
        margin: '0px 18px 8px 18px',
        padding: '8px 12px',
        borderRadius: '4px',
        borderLeft: 'solid 4px ' + unoTheme.warningColorStrong,
        background: unoTheme.secondaryTuplaTable,
        fontSize: '12px',
        color: unoTheme.defaultTextColor,

        '& .MuiSvgIcon-root': {
            marginRight: theme.spacing(1),
            fontSize: '18px',
            color: unoTheme.warningColorStrong,
        }
    },

    emptyMessage: {
        margin: '24px 18px',
        fontSize: '14px',
        color: unoTheme.defaultTextColor,
    },

    loadingWrapper: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '80px',
        color: unoTheme.mainColor,
    },

    groupRow: {
        cursor: 'pointer',
    },

    typeChip: {
        display: 'inline-block',
        padding: '2px 12px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: 600,
        textTransform: 'uppercase',
        color: 'white',
        whiteSpace: 'nowrap',
    },

    detailCell: {
        paddingTop: '0px !important',
        paddingBottom: '0px !important',
        background: 'white',
    },

    detailWrapper: {
        padding: '8px 0px 16px 0px',
    },

    detailTitle: {
        fontSize: '12px',
        fontWeight: 600,
        color: unoTheme.defaultTextColor,
        marginBottom: '4px',
    },

    value: {
        color: unoTheme.greenMoney,
    },
}));

const ENTITY_LABELS = {
    balances_validations: 'Saldos de fundos',
    balances_titles_validations: 'Saldos de títulos',
    transactions: 'Movimentações',
    client_rents: 'Rentabilidade da carteira',
};

const DETAIL_LIMIT = 200;

function getEntityLabel(entity) {
    return ENTITY_LABELS[entity] || entity;
}

function getTypeChipStyle(changeType) {

    return changeType === 'created' ?
        { background: unoTheme.successColor }
        : { background: unoTheme.warningColorStrong };
}

function getTypeLabel(changeType) {
    return changeType === 'created' ? 'Novo' : 'Editado';
}

function formatRefPeriod(row) {

    if (!row.ref_month || !row.ref_year) return '-';
    return getMonthName(row.ref_month) + '/' + row.ref_year;
}

function formatTimestamp(value) {
    return value ? moment(value).format('DD/MM/YYYY - HH:mm:ss') : '-';
}

/**
 * O período escolhido pelo usuário é uma data de calendário; convertemos para
 * os limites do dia em UTC, que é a referência usada para exibir datas em todo
 * o app e a mesma em que created_date/modified_date são gravados.
 */
function toUtcDayStart(date) {
    return date ? moment(date).format('YYYY-MM-DD') + 'T00:00:00.000Z' : null;
}

function toUtcDayEnd(date) {
    return date ? moment(date).format('YYYY-MM-DD') + 'T23:59:59.999Z' : null;
}

function getGroupKey(row) {
    return [row.entity, row.ref_year, row.ref_month, row.change_type].join('|');
}

export default function PortfolioChangesLogs({ selectedClient, dateStart, dateEnd }) {

    const classes = useStyles();

    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(false);
    const [expandedKey, setExpandedKey] = useState(null);
    const [detailsByKey, setDetailsByKey] = useState({});

    const doGetSummary = useCallback(async (clientId, startDate, endDate) => {

        setLoading(true);
        setExpandedKey(null);
        setDetailsByKey({});

        const response = await PortfolioChangesAPI.getChangesSummary(clientId, startDate, endDate);

        setSummary(response.success && response.body ? response.body.rows : []);
        setLoading(false);

    }, []);

    async function doGetDetail(row) {

        const key = getGroupKey(row);

        if (expandedKey === key) {
            setExpandedKey(null);
            return;
        }

        setExpandedKey(key);

        //Detalhe já carregado permanece em cache
        if (detailsByKey[key]) return;

        setDetailsByKey(prev => ({ ...prev, [key]: { loading: true } }));

        const response = await PortfolioChangesAPI.getChangesDetail(
            selectedClient?.id,
            toUtcDayStart(dateStart),
            toUtcDayEnd(dateEnd),
            row.entity,
            row.ref_month,
            row.ref_year,
            row.change_type,
            DETAIL_LIMIT,
            0);

        setDetailsByKey(prev => ({
            ...prev,
            [key]: response.success && response.body ?
                { rows: response.body.rows, total: response.body.total }
                : { rows: [], total: 0 }
        }));
    }

    useEffect(() => {

        if (!dateStart || !dateEnd) {
            setSummary(null);
            return;
        }

        doGetSummary(selectedClient?.id, toUtcDayStart(dateStart), toUtcDayEnd(dateEnd));

    }, [selectedClient, dateStart, dateEnd, doGetSummary]);

    function renderDetail(row) {

        const key = getGroupKey(row);
        const detail = detailsByKey[key];

        if (!detail || detail.loading) {
            return (
                <div className={classes.loadingWrapper}>
                    <CircularProgress size="20px" disableShrink color="inherit" />
                </div>
            )
        }

        if (!detail.rows || !detail.rows.length) {
            return <div className={classes.detailTitle}>{'Nenhum registro'}</div>
        }

        return (
            <div className={classes.detailWrapper}>
                <div className={classes.detailTitle}>
                    {detail.total > detail.rows.length ?
                        'Exibindo ' + detail.rows.length + ' de ' + detail.total + ' registros'
                        : detail.rows.length + ' registro(s)'}
                </div>
                <MyTable sizeheaders="verySmall" size="small" heightAuto noscroll>
                    <colgroup>
                        <col width="22%" />
                        <col width="48%" />
                        <col width="20%" />
                        <col width="10%" />
                    </colgroup>
                    <TableHead>
                        <TableRow>
                            <TableCell align="left">{'Alterado em'}</TableCell>
                            <TableCell align="left">{'Registro'}</TableCell>
                            <TableCell align="right">{'Valor'}</TableCell>
                            <TableCell align="right">{'ID'}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {detail.rows.map((detailRow, index) => (
                            <TableRow key={key + '-detail-' + index}>
                                <TableCell align="left">{formatTimestamp(detailRow.changed_at)}</TableCell>
                                <TableCell align="left">{detailRow.label}</TableCell>
                                <TableCell align="right" className={classes.value}>
                                    {detailRow.value != null ? applyMaskAmount(detailRow.value, true) : '-'}
                                </TableCell>
                                <TableCell align="right">{detailRow.record_id}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </MyTable>
            </div>
        )
    }

    return (
        <div className={classes.root}>

            {/* <div className={classes.warningBar}>
                <InfoOutlinedIcon />
                <span>
                    {'O período filtra a data da alteração; o mês de carteira afetado aparece na coluna "Mês de referência". '
                        + 'Exclusões de registros não são rastreadas — para resets e relançamentos, consulte a aba "Ações".'}
                </span>
            </div> */}

            {
                !dateStart || !dateEnd ?
                    <Typography className={classes.emptyMessage}>
                        {'Informe a data inicial e a data final para rodar a análise.'}
                    </Typography>
                    : loading ?
                        <div className={classes.loadingWrapper}>
                            <CircularProgress size="24px" disableShrink color="inherit" />
                        </div>
                        : summary && summary.length ?
                            <div className={'defaultScrollWithHeaderContent'}>
                                <MyTable stickyHeader nofixed size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell align="left">{'Entidade'}</TableCell>
                                            <TableCell align="left">{'Mês de referência'}</TableCell>
                                            <TableCell align="left">{'Tipo'}</TableCell>
                                            <TableCell align="right">{'Qtd.'}</TableCell>
                                            <TableCell align="left">{'Primeira alteração'}</TableCell>
                                            <TableCell align="left">{'Última alteração'}</TableCell>
                                            <TableCell align="right">{''}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {summary.map((row, index) => {

                                            const key = getGroupKey(row);
                                            const isExpanded = expandedKey === key;

                                            return (
                                                <React.Fragment key={'portfolio-change-' + index}>
                                                    <TableRow
                                                        className={classes.groupRow}
                                                        onClick={() => doGetDetail(row)}>
                                                        <TableCell align="left">{getEntityLabel(row.entity)}</TableCell>
                                                        <TableCell align="left">{formatRefPeriod(row)}</TableCell>
                                                        <TableCell align="left">
                                                            <span
                                                                className={classes.typeChip}
                                                                style={getTypeChipStyle(row.change_type)}>
                                                                {getTypeLabel(row.change_type)}
                                                            </span>
                                                        </TableCell>
                                                        <TableCell align="right">{row.total}</TableCell>
                                                        <TableCell align="left">{formatTimestamp(row.first_change)}</TableCell>
                                                        <TableCell align="left">{formatTimestamp(row.last_change)}</TableCell>
                                                        <TableCell align="right">
                                                            {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                                        </TableCell>
                                                    </TableRow>
                                                    <TableRow>
                                                        <TableCell className={classes.detailCell} colSpan={7}>
                                                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                                                {renderDetail(row)}
                                                            </Collapse>
                                                        </TableCell>
                                                    </TableRow>
                                                </React.Fragment>
                                            )
                                        })}
                                    </TableBody>
                                </MyTable>
                            </div>
                            : <Typography className={classes.emptyMessage}>
                                {'Nenhuma alteração no período.'}
                            </Typography>
            }

        </div>
    );
}
